/* eslint-disable @next/next/no-img-element */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../../../shadcn/components/ui/button";
import { Card } from "../../../../shadcn/components/ui/card";
import CustomModalLayoutV2 from "../../../bidder/Tabs/auctionFloor/components/CustomModalLayoutV2";

export interface ImageType {
  filePath: string;
  fileName: string;
}

export function PropertyImagePreview({
  sliderImages,
}: {
  sliderImages: ImageType[];
}) {
  const [open, setOpen] = useState(false);
  const [activeImage, setActiveImage] = useState<ImageType | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const listRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<number | null>(null);

  const handleOpen = (img: ImageType) => {
    const index = sliderImages.findIndex(
      (image) => image.filePath === img.filePath,
    );
    setCurrentIndex(index);
    setActiveImage(img);
    setOpen(true);
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setActiveImage(sliderImages[newIndex]);
    }
  };

  const handleNext = () => {
    if (currentIndex < sliderImages.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setActiveImage(sliderImages[newIndex]);
    }
  };

  const itemWidth = 192; // 180px image + 12px gap
  const scrollSpeed = 1; // Adjust for faster/slower scroll (pixels per frame)

  const animateScroll = useCallback(() => {
    if (!listRef.current) return;

    const container = listRef.current;
    const singleSetWidth = sliderImages.length * itemWidth;

    container.scrollLeft += scrollSpeed;

    // Seamless infinite loop: reset to start when we finish the first set
    if (container.scrollLeft >= singleSetWidth) {
      container.scrollLeft -= singleSetWidth;
    }

    animationRef.current = requestAnimationFrame(animateScroll);
  }, [sliderImages.length]);

  const startAutoScroll = useCallback(() => {
    if (animationRef.current || sliderImages.length <= 1) return;
    animationRef.current = requestAnimationFrame(animateScroll);
  }, [animateScroll, sliderImages.length]);

  const stopAutoScroll = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  // Check if content overflows container → only then enable auto-scroll
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  useEffect(() => {
    if (!listRef.current || sliderImages.length <= 1) {
      setShouldAutoScroll(false);
      stopAutoScroll();
      return;
    }

    const container = listRef.current;
    const totalContentWidth = sliderImages.length * itemWidth;
    const containerWidth = container.offsetWidth;

    const needsScroll = totalContentWidth > containerWidth;
    setShouldAutoScroll(needsScroll);

    if (needsScroll) {
      container.scrollLeft = 0;
      startAutoScroll();
    } else {
      stopAutoScroll();
    }

    // Re-check on resize
    const handleResize = () => {
      const updatedNeedsScroll = totalContentWidth > container.offsetWidth;
      setShouldAutoScroll(updatedNeedsScroll);
      if (!updatedNeedsScroll) stopAutoScroll();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      stopAutoScroll();
    };
  }, [sliderImages.length, startAutoScroll, stopAutoScroll]);

  return (
    <div>
      <Card className="bg-white shadow-card-shadow p-4 border-none rounded-[10px]">
        <h3 className="text-lg font-openSans font-semibold text-brand-textBlack capitalize">
          Property Photos
        </h3>
        {sliderImages.length > 0 ? (
          <div
            ref={listRef}
            className="flex gap-3 py-3 overflow-x-auto scroll-smooth scrollbar-hide"
            onMouseEnter={stopAutoScroll}
            onMouseLeave={startAutoScroll}
          >
            <div className="flex flex-shrink-0 gap-3">
              {sliderImages?.map((img, index) => (
                <img
                  key={`original-${index}`}
                  src={img.filePath}
                  alt={img.fileName}
                  onClick={() => handleOpen(img)}
                  // onError={(e) => {
                  //   e.currentTarget.src = FALLBACK_IMAGE;
                  // }}
                  className="flex-shrink-0 rounded-[6px] w-[180px] h-[110px] object-cover hover:scale-105 transition-transform duration-200 cursor-pointer"
                  loading="lazy"
                />
              ))}
            </div>

            {shouldAutoScroll && (
              <div className="flex flex-shrink-0 gap-3">
                {sliderImages.map((img, index) => (
                  <img
                    key={`duplicate-${index}`}
                    src={img.filePath}
                    alt={img.fileName}
                    onClick={() => handleOpen(img)}
                    // onError={(e) => {
                    //   e.currentTarget.src = FALLBACK_IMAGE;
                    // }}
                    className="flex-shrink-0 bg-gray-200 rounded-[6px] w-[180px] h-[110px] object-cover hover:scale-105 transition-transform duration-200 cursor-pointer"
                    loading="lazy"
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex justify-center items-center h-[110px]">
            <div className="flex flex-col justify-center items-center text-center">
              <p className="mt-10 font-openSans text-brand-textBlack text-sm">
                No photos available right now
              </p>
            </div>
          </div>
        )}
      </Card>

      <CustomModalLayoutV2
        open={open}
        blurBackground={true}
        onClose={() => setOpen(false)}
        childrenClassName="pb-6 px-6 lg:px-8 overflow-y-scroll"
        className="pt-8 lg:pb-[30px] mx-4 w-full max-w-[600px] max-h-[90vh]"
      >
        <h2 className="pt-4 font-semibold text-gray-900 text-lg mb-2">
          Property Photo{sliderImages.length > 1 && "'s"}
        </h2>
        <div className="overflow-y-scroll">
          {activeImage?.filePath ? (
            <img
              src={activeImage?.filePath}
              alt={activeImage?.fileName}
              className="flex-shrink-0 bg-gray-200 max-h-[400px] rounded-[6px] w-full object-cover"
              loading="lazy"
            />
          ) : null}
        </div>
        {sliderImages.length > 1 && (
          <div className="flex justify-between mt-4">
            <Button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="bg-brand-primaryAction hover:bg-brand-primaryAction"
            >
              Previous
            </Button>
            <Button
              onClick={handleNext}
              disabled={currentIndex === sliderImages.length - 1}
              className="bg-brand-primaryAction hover:bg-brand-primaryAction"
            >
              Next
            </Button>
          </div>
        )}
      </CustomModalLayoutV2>
    </div>
  );
}
